import BaseService from 'services/BaseService';
import {
  AdStatus,
  BatchStatus,
  CandidatesResponse,
  LaunchCreative,
  LaunchRow,
  LaunchSettings,
  PreviewResponse,
  QueuedBatch,
  TemplateSummary,
} from 'pages/AdLaunch/types';

/**
 * Ledger rows come straight out of Snowflake, and the driver's column casing
 * has differed between paths. Every row is lower-cased here, once, so the
 * pages read one shape (`LaunchRow`) and a casing change cannot blank a table
 * or crash a status panel.
 */
export function lowerKeys<T>(row: object): T {
  return Object.fromEntries(Object.entries(row).map(([k, v]) => [k.toLowerCase(), v])) as T;
}

class AdLaunchService extends BaseService {
  basePath = 'v1/ad-launch';

  /**
   * @param refresh re-read every page body from Notion instead of reusing the
   *   parses the API already holds. Row properties are always fresh either way.
   */
  async getCandidates(refresh = false) {
    await this._getAccessToken();
    return this.handleHttpRequest<CandidatesResponse>(
      () => this.http.get(`${this.basePath}/candidates${refresh ? '?refresh=1' : ''}`),
      'Failed to load launch candidates',
    );
  }

  /**
   * Rows already tagged Launched. Deliberately its own call: they are five
   * times as numerous as the launchable rows and Notion pages 100 at a time,
   * so asking for them alongside the candidates cost four extra round trips on
   * every load to fill a list that is usually never opened.
   */
  async getLaunched() {
    await this._getAccessToken();
    return this.handleHttpRequest<{ launched: LaunchCreative[] }>(
      () => this.http.get(`${this.basePath}/launched`),
      'Failed to load already-launched creatives',
    );
  }

  async getPreview(notionPageIds: string[], templateKey: string, adStatus?: AdStatus) {
    await this._getAccessToken();
    // Deliberately NOT handleHttpRequest: a preview fails for reasons the
    // operator has to read (already launched, template renamed, too few site
    // links), and that wrapper turns every one of them into a bare null.
    const res = await this.http.get(`${this.basePath}/preview`, {
      params: { ids: notionPageIds.join(','), templateKey, ...(adStatus ? { adStatus } : {}) },
    });
    return res.data as PreviewResponse;
  }

  async launch(notionPageIds: string[], templateKey: string, adStatus?: AdStatus, requestedBy?: string) {
    await this._getAccessToken();
    const res = await this.http.post(`${this.basePath}/batches`, { notionPageIds, templateKey, adStatus, requestedBy });
    return res.data as QueuedBatch;
  }

  async getBatchStatus(batchIds: string[]) {
    await this._getAccessToken();
    const res = await this.handleHttpRequest<{ batches: Record<string, BatchStatus> }>(
      () => this.http.get(`${this.basePath}/batches`, { params: { ids: batchIds.join(',') } }),
      'Failed to load batch status',
    );
    if (!res) return res;
    const batches: Record<string, BatchStatus> = {};
    for (const [id, batch] of Object.entries(res.batches)) {
      batches[id] = { ...batch, launches: batch.launches.map(l => lowerKeys<LaunchRow>(l)) };
    }
    return { batches };
  }

  async getLaunches(limit = 100) {
    await this._getAccessToken();
    const res = await this.handleHttpRequest<{ accountId: string; launches: LaunchRow[] }>(
      () => this.http.get(`${this.basePath}/launches`, { params: { limit } }),
      'Failed to load launch history',
    );
    if (!res) return res;
    return { ...res, launches: res.launches.map(l => lowerKeys<LaunchRow>(l)) };
  }

  async getSettings() {
    await this._getAccessToken();
    return this.handleHttpRequest<LaunchSettings>(
      () => this.http.get(`${this.basePath}/settings`),
      'Failed to load ad launcher settings',
    );
  }

  /** The saved template, optionally with a field-level diff against the live campaign. */
  async getTemplate(key: string, diff = false) {
    await this._getAccessToken();
    const res = await this.http.get(`${this.basePath}/template`, { params: { key, diff: diff ? 'true' : undefined } });
    return res.data as {
      key: string;
      template: Record<string, unknown>;
      live?: Record<string, unknown>;
      diff?: { path: string; saved: unknown; live: unknown }[];
    };
  }

  /** Capture an ad, ad set or campaign as a template, saved under a name. */
  async captureTemplate(name: string, id: string, adType: string) {
    await this._getAccessToken();
    const res = await this.http.post(`${this.basePath}/template`, { name, id, adType });
    return res.data as { key: string; template: TemplateSummary };
  }

  /** Edit a saved template in place — rename, or curate its site links. */
  async updateTemplate(patch: { key: string; name?: string; siteLinksEnabled?: boolean; siteLinksSelected?: string[] }) {
    await this._getAccessToken();
    const res = await this.http.patch(`${this.basePath}/template`, patch);
    return res.data as { key: string; template: TemplateSummary };
  }

  /** Re-read the exact ad set and ad this template was built from. */
  async resyncTemplate(key: string) {
    await this._getAccessToken();
    const res = await this.http.post(`${this.basePath}/template/resync`, { key });
    return res.data as {
      key: string;
      template: TemplateSummary;
      changed: number;
      diff: { path: string; saved: unknown; live: unknown }[];
    };
  }

  async deleteTemplate(key: string) {
    await this._getAccessToken();
    const res = await this.http.delete(`${this.basePath}/template`, { params: { key } });
    return res.data as { deleted: string };
  }
}

export default AdLaunchService;
